import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";

// Type-predicate validators for the persisted page state. Each guards one stored blob
// for usePersistentState / readJSON: a corrupt or stale value fails the predicate and the feature
// falls back to its default. Returning `v is T` (not bare boolean) lets the storage seam narrow the
// parsed value with no cast.

/** A string (the simple selection ids: boardId, gameId, mode, race, ...). */
export const isStr = (v: unknown): v is string => typeof v === "string";

/** A non-null, non-array object: the base shape every record validator narrows from. */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** A string-valued map (the per-board layer / per-Game selection records). Reject non-string values. */
export const isStrRecord = (v: unknown): v is Record<string, string> =>
  isRecord(v) && Object.values(v).every((x) => typeof x === "string");

/** An uploaded Profile's durable truth: its character-space text plus a name, or null. */
export const isUpload = (v: unknown): v is { text: string; name: string } | null =>
  v === null || (isRecord(v) && typeof v.text === "string" && typeof v.name === "string");

/**
 * A per-Game map of uploaded simple-Game Profiles, each its character-space text + name (Destiny's
 * "my own" cvars upload, keyed by gameId). Re-ingested through the Game's parser per view, never
 * stored decoded; a corrupt entry drops the whole blob back to {} (cf. isUpload).
 */
export const isUploadRecord = (v: unknown): v is Record<string, { text: string; name: string }> =>
  isRecord(v) &&
  Object.values(v).every(
    (x) => isRecord(x) && typeof x.text === "string" && typeof x.name === "string",
  );

/**
 * One decoded board layer. A KLE layer carries chars only; a QMK/VIA layer carries the
 * per-key keycodes, their resolved active codes, and legends. Validating the variant matters: a layer
 * persisted before the resolved-codes shape (no `kind`/`codes`) would throw at render (compose /
 * project deref layer.codes[i]), so such a stale blob must fail here and fall back to {}.
 */
const isBoardLayer = (v: unknown): boolean => {
  if (!isRecord(v)) return false;
  if (typeof v.id !== "string" || typeof v.name !== "string") return false;
  if (!Array.isArray(v.chars) || typeof v.empty !== "boolean") return false;
  if (v.kind === "kle") return true;
  if (v.kind === "qmk")
    return Array.isArray(v.keycodes) && Array.isArray(v.codes) && Array.isArray(v.legends);
  return false;
};

/**
 * A map of decoded session boards. Validates the structure, not just the wrapper: a corrupt entry
 * would throw downstream (the Keyboard picker derefs entry.keyboard.id; compose maps
 * keyboard.keys; activeLayout / layoutGroups / charMapOf walk entry.boardLayers and read the first
 * layer), so a rejected blob falls back to {} (cf. isUpload). The keyboard must carry its keys
 * array and at least one board layer - every real import has both (KLE builds one legends layer,
 * VIA/QMK several); a blob missing either restores to a white screen.
 */
export const isImportedRecord = (v: unknown): v is Record<string, ImportedKeyboard> =>
  isRecord(v) &&
  Object.values(v).every((e) => {
    if (!isRecord(e)) return false;
    const k = e.keyboard;
    return (
      isRecord(k) &&
      typeof k.id === "string" &&
      typeof k.name === "string" &&
      Array.isArray(k.keys) &&
      Array.isArray(e.boardLayers) &&
      e.boardLayers.length > 0 &&
      e.boardLayers.every(isBoardLayer) &&
      typeof e.format === "string"
    );
  });
